<template>
  <div class="game-main">
    <CatalogModules
      v-if="isNewCatalogShown"
      :json-data="activeGameData.content" />

    <template v-else>
      <base-card-list
        v-if="hotOffers.length"
        ref="hotOffers"
        :scrollable-from="1023"
        :items="hotOffers"
        :visible-rows="hotOffersPage"
        :show-load-more="showLoadMore.hot"
        class="game-main__offers container"
        @load-more="loadMore('hotOffers')">
        <template #title>
          {{ BadgesModel.HOT.label }}
        </template>
        <template #card="{ item }">
          <product-card
            :product="item"
            class="game-main__offer" />
        </template>
      </base-card-list>

      <game-banner-list
        v-if="banners.length"
        :banners="banners"
        class="game-main__banners container" />

      <need-help class="game-main__need-help container" />

      <base-card-list
        v-if="popularOffers.length"
        :scrollable-from="1023"
        :items="popularOffers"
        :visible-rows="popularOffersPage"
        :show-load-more="showLoadMore.popular"
        class="game-main__offers container"
        @load-more="loadMore('popularOffers')">
        <template #title>
          {{ BadgesModel.POPULAR.label }}
        </template>

        <template #card="{ item }">
          <product-card
            :product="item"
            class="game-main__offer" />
        </template>
      </base-card-list>
    </template>

    <div class="game-main__categories-wrapper container">
      <h2 class="game-main__categories-title">
        Our Services
      </h2>

      <ul class="game-main__categories anti-content">
        <li
          v-for="category in rootTagsByGames[activeGame.id]"
          :key="category.title"
          class="game-main__category">
          <router-link
            :to="category._href"
            class="game-main__category-link">
            {{ category.title }}
          </router-link>
        </li>
      </ul>
    </div>

    <how-it-works class="container game-main__how-it-works" />

    <we-try-hard class="game-main__we-try-hard" />

    <reviews-slider
      v-if="reviews.length"
      :key="activeGame.id"
      title="SkyCoach reviews"
      :reviews="reviews"
      :breakpoints="sliderBreakpoints"
      class="container game-main__reviews" />

    <follow-us class="game-main__follow-us container" />

    <footer-seo-text
      :text="seoTextToShow"
      class="game-main__seo-text container" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import GameModel from 'Models/GameModel';
import ReviewsSlider from 'Components/Reviews/ReviewsSlider.vue';
import HowItWorks from 'Root/pages/game/components/HowItWorks.vue';
import WeTryHard from 'Root/pages/game/components/WeTryHard.vue';
import FollowUs from 'Root/pages/game/components/FollowUs.vue';
import GameBannerList from 'Root/pages/game/components/GameBannerList.vue';
import NeedHelp from 'Root/pages/game/components/NeedHelp.vue';
import SeoModel from 'Models/SeoModel';
import seoMixin from 'Root/mixins/seoMixin';
import AppModel from 'Models/AppModel';
import BadgesModel from 'Models/BadgesModel';
import FooterSeoText from 'Components/FooterSeoText.vue';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import { getFilenameByNamespace } from 'Root/helpers/seo';
import BaseCardList from 'Components/ui/BaseCardList/BaseCardList.vue';
import ProductCard from 'Root/pages/game/components/ProductCard.vue';
import { rootTagsByGames } from 'Root/helpers/tags';
import CatalogModules from 'Root/pages/game/components/CatalogModules.vue';
import { ABTestPriceDecreaseForNewUsers } from 'Root/drivers/ABTestsDriver';

const defaultOffersPerPage = 6;

export default {
  name: 'GameMain',

  components: {
    FooterSeoText,
    GameBannerList,
    NeedHelp,
    ReviewsSlider,
    HowItWorks,
    WeTryHard,
    FollowUs,
    BaseCardList,
    ProductCard,
    CatalogModules,
  },

  mixins: [seoMixin],

  seo() {
    let title = AppModel.APP_TITLE;
    let description = null;

    if (this.$route.name === 'game__root') {
      title = SeoModel.metaByGameId[this.activeGame.id].title;
      description = SeoModel.metaByGameId[this.activeGame.id].description;
    }

    const urn = typeof window === 'undefined'
      ? this.$ssrContext.url
      : window.location.pathname;

    const {
      lowPrice, highPrice, ratingValue, ratingCount, total,
    } = this.catalogProductsMarkup[this.activeGame.id] ?? {};

    const product = {
      name: title,
      description,
      offer: {
        type: 'AggregateOffer',
        url: this.$route.fullPath,
        priceCurrency: this.activeRegion.currency.title,
        lowPrice: lowPrice?.[this.activeRegion.shortTitle.toLowerCase()],
        highPrice: highPrice?.[this.activeRegion.shortTitle.toLowerCase()],
        offerCount: total,
        inStock: !!total,
      },
      rating: {
        value: ratingValue,
        count: ratingCount,
      },
    };

    return {
      title,
      meta: {
        description,
        'og:type': 'website',
        'og:title': title,
        'og:url': `${AppModel.URL}${urn}`,
        'og:image': `${GameModel.getGameBackground(this.activeGame.id)}-1400.jpg`,
        'og:description': description,
      },
      product,
    };
  },

  asyncData({ store, context }) {
    const todayTopPromise = store.dispatch('products/getTodayTop', store.state.activeGame.id);
    const seoTextPromise = store.dispatch('getSeoText', getFilenameByNamespace(store.state.activeGame.routeNamespace))
      .then(({ data }) => {
        context.seoText = data;
      })
      .catch((e) => console.log(e.message));

    const catalogJSONPromise = Object.keys(context.getCatalogJSONData()).length !== 0
      ? store.dispatch('setCatalogJSONData', context.getCatalogJSONData())
        .then(() => store.dispatch('fetchCatalogProductsByGameId', store.state.activeGame.id))
      : store.dispatch('fetchCatalogProductsByGameId', store.state.activeGame.id);

    return Promise.all([todayTopPromise, seoTextPromise, catalogJSONPromise]);
  },

  data() {
    return {
      hotOffersPage: 1,
      popularOffersPage: 1,
      offersPerPage: defaultOffersPerPage,
      sliderBreakpoints: {
        1380: {
          perView: 2,
        },
        1760: {
          perView: 3,
        },
        2560: {
          perView: 4,
        },
      },
      BadgesModel,
      seoTextToShow: null,
      rootTagsByGames,
      isPriceDecreaseUserAdded: false,
    };
  },

  computed: {
    ...mapState([
      'activeGame',
      'activeRegion',
      'userDevice',
      'products',
      'reviews',
      'catalogJSONData',
      'catalogProductsMarkup',
    ]),
    ...mapState('products', ['todayTop', 'activeTag']),
    ...mapGetters('products', ['breadcrumbs']),
    ...mapGetters(['isUsersFirstOrder']),

    hotOffers() {
      return this.getOffers(BadgesModel.HOT.id, this.hotOffersPage, this.offersPerPage);
    },

    popularOffers() {
      return this.getOffers(BadgesModel.POPULAR.id, this.popularOffersPage, this.offersPerPage);
    },

    isWow() {
      return this.activeGame.id === GameModel.WOW.id;
    },

    showLoadMore() {
      return {
        hot: this.hotOffersPage * this.offersPerPage < this.hotOffers.length,
        popular: this.popularOffersPage * this.offersPerPage < this.popularOffers.length,
      };
    },

    isMobile() {
      return this.userDevice.innerWidth < 1024;
    },

    banners() {
      return this.products.tags
        .filter((tag) => tag.is_promo && tag.game_id === this.activeGame.id)
        .map((tag) => ({ ...tag, url: tag._href }));
    },

    activeGameData() {
      if (!Array.isArray(this.catalogJSONData)) {
        return null;
      }

      return this.catalogJSONData.find((data) => this.activeGame.id === data.game_id);
    },

    isNewCatalogShown() {
      return this.activeGameData && this.activeGameData.is_active && this.activeGameData.content.length;
    },
  },

  watch: {
    'activeGame.id': {
      handler(newVal) {
        this.hotOffersPage = 1;
        this.popularOffersPage = 1;

        if (this.todayTop._gameId !== newVal) {
          this.getTodayTop(newVal);
        }

        this.fetchAndSetSeoText(this.activeGame.routeNamespace);
      },
    },

    'userDevice.innerWidth': {
      handler() {
        this.offersPerPage = this.getCardsCountInRow();
      },
    },

    isPriceDecreaseUserAdded(val) {
      if (val) {
        const {
          testId,
          groupId,
        } = ABTestPriceDecreaseForNewUsers.userGroup;

        googleTagAnalytics.fireGtagEvent(`ab_test_${testId}_${groupId}`);
      }
    },
  },

  async created() {
    if (this.activeTag) {
      this.$store.commit('products/mutateActiveTag', null);
    }

    if (this.$isSSR) {
      this.seoTextToShow = this.$ssrContext.seoText;

      delete this.$ssrContext.seoText;
    } else {
      this.$store.dispatch('updateReviewsImagesByProducts');
      const seoText = document.querySelector('.game-main__seo-text .seo-text__content');
      if (!this.seoTextToShow && seoText) {
        this.seoTextToShow = seoText.innerHTML;
      } else {
        this.fetchAndSetSeoText(this.activeGame.routeNamespace);
      }

      if (this.$store.state.catalogJSONData === null) {
        this.loadCatalogData();
      }
    }

    if (
      this.isWow
            && this.$route.query?.utm_medium === 'cpc'
            && (!this.user || this.isUsersFirstOrder)
    ) {
      if (await ABTestPriceDecreaseForNewUsers.addUserToTest()) {
        this.isPriceDecreaseUserAdded = true;
      }
    }
  },

  async mounted() {
    if (this.todayTop._gameId !== this.activeGame.id) {
      await this.getTodayTop(this.activeGame.id);
    }

    this.offersPerPage = this.getCardsCountInRow();

    googleTagAnalytics.emitProductListNew(
      this.activeGame.fullTitle,
      [...this.hotOffers, ...this.popularOffers],
      this.$store.state.activeRegion,
    );
  },

  methods: {
    ...mapActions('products', ['getTodayTop', 'getProductById']),

    async loadCatalogData() {
      const response = await this.$axios
        .get(`/storage/${process.env.FILESYSTEM_RESOURCE_CATALOG_DATA_PATH}?v=${Date.now()}`);
      await this.$store.dispatch('setCatalogJSONData', response.data);
    },

    getOffers(badgeId, page, perPage) {
      if (!this.todayTop.offers?.[badgeId]) {
        return [];
      }

      let offers = this.todayTop.offers[badgeId];

      const regionId = this.$store.state.activeRegion?.id;
      if (regionId) {
        offers = offers.filter((offer) => offer.available_in_regions.includes(regionId));
      }

      // offers = offers.sort((o1, o2) => o2.priority - o1.priority);

      // if (regionId) {
      //   offers = offers.sort((o1, o2) => +o2.available_in_regions.includes(regionId) - +o1.available_in_regions.includes(regionId));
      // }

      if (this.isMobile) {
        offers = offers.slice(0, page * perPage) ?? [];
      }

      return offers;
    },

    loadMore(entity) {
      this[`${entity}Page`] += 1;
    },

    getCardsCountInRow() {
      if (!this.isMobile) {
        let count = 0;
        const containerNode = this.$refs.hotOffers?.$el;

        if (containerNode) {
          const containerWidth = containerNode.getBoundingClientRect().width;
          const minCardWidth = 250;
          const columnGap = 16;
          count = Math.floor((containerWidth + columnGap) / (minCardWidth + columnGap));
        }
        return count;
      }

      return defaultOffersPerPage;
    },

    fetchAndSetSeoText(namespace) {
      this.$store.dispatch('getSeoText', getFilenameByNamespace(namespace))
        .then(({ data }) => {
          this.seoTextToShow = data;
        })
        .catch(() => {
          this.seoTextToShow = null;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
  .game-main .game-main__offers {
    --item-width: 250px;
  }

  .game-main__how-it-works {
    padding-bottom: 0;
  }

  .game-main__we-try-hard {
    padding: 25px 0;
  }

  .game-main__reviews {
    padding-top: 0;
  }

  .game-main__categories-title {
    font-size: 21px;
    font-weight: 500;
    margin-bottom: 16px;
  }

  .game-main__categories {
    @include hide-scrollbar;

    -ms-overflow-style: none;
    scrollbar-width: none;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    scroll-padding-left: var(--padding);
    padding-left: var(--padding);
    padding-right: var(--padding);
    overflow-x: scroll;
    display: grid;
    grid-auto-flow: column;
    grid-gap: 8px;
  }

  .game-main__category {
    scroll-snap-align: start;
    background-color: rgba($color--basic-blue, 0.25);
    border-radius: 8px;
    border: 1px solid rgba($color--basic-blue, 0.25);
    text-align: center;
  }

  .game-main__category-link {
    font-size: 15px;
    font-weight: 500;
    padding: 12px 16px;
    color: $color--basic-white;
    white-space: nowrap;
    display: inline-block;
  }

  @media screen and #{$screen-less-than-1024} {
    /deep/ .game-main__reviews {
      padding: 12px 0;

      .reviews__header {
        margin-left: var(--padding);
        margin-right: var(--padding);
      }

      .reviews__body {
        padding: 0 var(--padding);
        scroll-padding-left: var(--padding);
      }
    }
  }

  @media screen and #{$screen-more-or-1024} {
    .game-main__we-try-hard {
      padding: 65px 0 25px;
    }

    .game-main__categories-wrapper {
      display: none;
    }

    .catalog-modules {
      /deep/.need-help__wrapper {
        padding: 32px 88px;
      }

      /deep/.need-help__icon {
        margin-right: 24px;
      }

      /deep/.need-help__title {
        margin-right: 24px;
      }

      /deep/.need-help__message {
        margin-right: 24px;
        font-weight: 300;
      }
    }
  }
</style>
